import { useCallback, useMemo, useRef, useState } from 'react';
import useIsMobileDevice from 'src/hooks/useIsMobileDevice';

interface Params {
	valueFromProps: number;
	onValueChange: (value: number) => void;
	onValueCommit: (value: number) => void;
	disabled?: boolean;
}

export default function useSlider({
	valueFromProps,
	onValueChange,
	onValueCommit,
	disabled,
}: Params) {
	const thumbRef = useRef<HTMLButtonElement>(null);
	const trackRef = useRef<HTMLDivElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const isMobileScreenSize = useIsMobileDevice();

	// When using a trackpad, it's possible for the pointer capture to be lost,
	// which results in `handleValueCommit` not being called. So we need to
	// manually commit the latest value via `handleLostPointerCapture`.
	// See https://github.com/radix-ui/primitives/issues/1760 for more details.
	const cachedValue = useRef(valueFromProps);
	const shouldCommit = useRef(false);

	// Radix expects an array of numbers for `value` (because it's possible to
	// have multiple thumbs). So we have to wrap our external value in an array.
	const value = useMemo(() => {
		return [isNaN(valueFromProps) ? 0 : valueFromProps];
	}, [valueFromProps]);

	const handleValueChange = useCallback(
		(value: [number]) => {
			if (disabled) {
				return;
			}

			onValueChange(value[0]);

			cachedValue.current = value[0];
			shouldCommit.current = true;

			setIsDragging(true);
		},
		[onValueChange, disabled]
	);

	const handleValueCommit = useCallback(
		(value: [number]) => {
			if (disabled) {
				return;
			}

			onValueCommit(value[0]);

			shouldCommit.current = false;

			setIsDragging(false);
		},
		[onValueCommit, disabled]
	);

	const handleLostPointerCapture = useCallback(() => {
		if (cachedValue.current !== undefined && shouldCommit.current) {
			handleValueCommit([cachedValue.current]);
		}
	}, [handleValueCommit]);

	const handleMarkClick = useCallback(
		(markValue: number) => {
			// On mobile, moving the thumb can trigger unexpected click events on marks.
			// So we instead use `onTouchStart` for the marks on mobile.
			if (disabled || isMobileScreenSize) {
				return;
			}

			handleValueCommit([markValue]);
		},
		[disabled, isMobileScreenSize, handleValueCommit]
	);

	/**
	 * Pointer down also fires a change event, so we need to pass the mark value
	 * for it as well. Otherwise, the slider will first go to the pointer down
	 * location (which could be slightly different than the mark value), then
	 * go to the mark value on pointer up (i.e. when click is triggered).
	 */
	const handleMarkPointerDown = useCallback(
		(markValue: number, e: React.PointerEvent<HTMLButtonElement>) => {
			if (disabled || isMobileScreenSize) {
				return;
			}

			e.preventDefault();

			handleValueCommit([markValue]);

			// Focus the button programmatically for the same reason documented
			// in `handleThumbPointerDown`.
			e.currentTarget.focus();
		},
		[disabled, isMobileScreenSize, handleValueCommit]
	);

	const handleMarkTouchStart = useCallback(
		(markValue: number) => {
			if (disabled) {
				return;
			}

			handleValueCommit([markValue]);
		},
		[disabled, handleValueCommit]
	);

	const handleThumbPointerDown = useCallback(() => {
		if (disabled) {
			return;
		}

		// Used to programmatically take focus away from any focused input field.
		// Necessary on mobile, because if the user was previously editing a size
		// input, and then directly begins dragging the slider, the blur event won't
		// get called on the size input and in turn its value won't update as the
		// slider is dragged.
		thumbRef.current?.focus();
	}, [disabled]);

	return {
		value,
		isDragging,
		handleValueChange,
		handleValueCommit,
		handleLostPointerCapture,
		handleMarkClick,
		handleMarkPointerDown,
		handleMarkTouchStart,
		handleThumbPointerDown,
		thumbRef,
		trackRef,
	};
}
